class BucketBunkerGallery extends HTMLElement {
  connectedCallback() {
    this.primaryImage = this.querySelector('.bb-product-gallery__primary img');
    this.buttons = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    if (!this.primaryImage || this.buttons.length === 0) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.select(button);
      });
    });
  }

  select(button) {
    const source = button.dataset.mediaSource;
    const alt = button.dataset.mediaAlt || '';
    if (!source) return;

    // Once a thumbnail becomes primary, the original responsive candidates no
    // longer describe that image and must not override the selected source.
    this.primaryImage.removeAttribute('srcset');
    this.primaryImage.removeAttribute('sizes');
    this.primaryImage.src = source;
    this.primaryImage.alt = alt;
    this.primaryImage.classList.toggle(
      'bb-product-gallery__primary-image--cutout',
      button.dataset.galleryCutout === 'true'
    );
    this.buttons.forEach((candidate) => candidate.setAttribute('aria-current', String(candidate === button)));
  }
}

class BucketBunkerSiteHeader extends HTMLElement {
  connectedCallback() {
    this.desktopDisclosures = Array.from(this.querySelectorAll('[data-bb-desktop-disclosure]'));
    this.mobileMenu = this.querySelector('[data-bb-mobile-menu]');
    this.mobileDisclosures = Array.from(this.querySelectorAll('[data-bb-mobile-disclosure]'));
    this.desktopQuery = window.matchMedia('(min-width: 1100px)');

    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleDesktopToggle = this.handleDesktopToggle.bind(this);
    this.handleMobileToggle = this.handleMobileToggle.bind(this);
    this.handleMobileDisclosureToggle = this.handleMobileDisclosureToggle.bind(this);

    this.desktopDisclosures.forEach((details) => {
      details.addEventListener('toggle', this.handleDesktopToggle);
      this.syncExpanded(details);
    });

    if (this.mobileMenu) {
      this.mobileMenu.addEventListener('toggle', this.handleMobileToggle);
      this.syncExpanded(this.mobileMenu);
    }

    this.mobileDisclosures.forEach((details) => {
      details.addEventListener('toggle', this.handleMobileDisclosureToggle);
      this.syncExpanded(details);
    });

    document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    document.addEventListener('keydown', this.handleKeyDown);

    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.handleBreakpointChange);
    } else {
      this.desktopQuery.addListener(this.handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    this.desktopDisclosures?.forEach((details) => details.removeEventListener('toggle', this.handleDesktopToggle));
    this.mobileMenu?.removeEventListener('toggle', this.handleMobileToggle);
    this.mobileDisclosures?.forEach((details) => details.removeEventListener('toggle', this.handleMobileDisclosureToggle));
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    document.removeEventListener('keydown', this.handleKeyDown);

    if (this.desktopQuery?.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.handleBreakpointChange);
    } else {
      this.desktopQuery?.removeListener(this.handleBreakpointChange);
    }

    document.body.classList.remove('bb-nav-open');
  }

  syncExpanded(details) {
    const summary = details?.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', String(details.open));
  }

  closeDetails(details, restoreFocus = false) {
    if (!details?.open) return;
    const summary = details.querySelector(':scope > summary');
    details.open = false;
    this.syncExpanded(details);
    if (restoreFocus) summary?.focus();
  }

  handleDesktopToggle(event) {
    const details = event.currentTarget;
    this.syncExpanded(details);
    if (!details.open) return;

    this.desktopDisclosures.forEach((candidate) => {
      if (candidate !== details) this.closeDetails(candidate);
    });
  }

  handleMobileToggle() {
    this.syncExpanded(this.mobileMenu);
    document.body.classList.toggle('bb-nav-open', Boolean(this.mobileMenu?.open));

    if (!this.mobileMenu?.open) {
      this.mobileDisclosures.forEach((details) => this.closeDetails(details));
    }
  }

  handleMobileDisclosureToggle(event) {
    const details = event.currentTarget;
    this.syncExpanded(details);
    if (!details.open) return;

    this.mobileDisclosures.forEach((candidate) => {
      if (
        candidate !== details
        && !candidate.contains(details)
        && !details.contains(candidate)
      ) {
        this.closeDetails(candidate);
      }
    });
  }

  handleDocumentPointerDown(event) {
    if (!this.desktopQuery.matches || this.contains(event.target)) return;
    this.desktopDisclosures.forEach((details) => this.closeDetails(details));
  }

  handleBreakpointChange(event) {
    if (event.matches) {
      this.closeDetails(this.mobileMenu);
      document.body.classList.remove('bb-nav-open');
    } else {
      this.desktopDisclosures.forEach((details) => this.closeDetails(details));
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      if (this.mobileMenu?.open) {
        event.preventDefault();
        this.closeDetails(this.mobileMenu, true);
        document.body.classList.remove('bb-nav-open');
        return;
      }

      const openDesktop = this.desktopDisclosures.find((details) => details.open);
      if (openDesktop) {
        event.preventDefault();
        this.closeDetails(openDesktop, true);
      }
      return;
    }

    if (event.key !== 'Tab' || !this.mobileMenu?.open || this.desktopQuery.matches) return;

    const focusable = Array.from(
      this.mobileMenu.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((node) => node.getClientRects().length > 0);

    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class BucketBunkerProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantData = this.querySelector('[data-variant-json]');
    this.price = this.querySelector('[data-product-price]');
    this.sku = this.querySelector('[data-product-sku]');
    this.addButton = this.querySelector('button[name="add"]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.quoteLink = this.querySelector('[data-product-quote]');

    if (!this.form || !this.variantData) return;

    try {
      this.variants = JSON.parse(this.variantData.textContent);
    } catch (_error) {
      return;
    }

    this.form.addEventListener('change', (event) => {
      if (event.target.name === 'id') this.selectVariant(event.target.value);
    });
  }

  selectVariant(id) {
    const variant = this.variants.find((candidate) => String(candidate.id) === String(id));
    if (!variant) return;

    if (this.price && variant.formatted_price) this.price.textContent = variant.formatted_price;
    if (this.sku) this.sku.textContent = variant.sku ? `SKU ${variant.sku}` : '';
    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      if (this.addLabel) this.addLabel.textContent = variant.available ? 'Add to Cart' : 'Unavailable';
    }

    if (this.quoteLink?.dataset.quoteBase) {
      const quoteUrl = new URL(this.quoteLink.dataset.quoteBase, window.location.origin);
      quoteUrl.searchParams.set('model', this.dataset.productTitle || '');
      quoteUrl.searchParams.set('product_handle', this.dataset.productHandle || '');
      quoteUrl.searchParams.set('variant', variant.id);
      if (variant.sku) quoteUrl.searchParams.set('sku', variant.sku);
      else quoteUrl.searchParams.delete('sku');
      quoteUrl.hash = 'quote-form';
      this.quoteLink.href = quoteUrl.toString();
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', nextUrl);
  }
}

if (!customElements.get('bb-product-gallery')) {
  customElements.define('bb-product-gallery', BucketBunkerGallery);
}

if (!customElements.get('bb-site-header')) {
  customElements.define('bb-site-header', BucketBunkerSiteHeader);
}

if (!customElements.get('bb-product-form')) {
  customElements.define('bb-product-form', BucketBunkerProductForm);
}

document.querySelectorAll('[data-bb-inquiry-panel]').forEach((panel) => {
  const modelInput = panel.querySelector('[data-bb-inquiry-model]');
  if (!modelInput || modelInput.value) return;

  const params = new URLSearchParams(window.location.search);
  const model = params.get('model');
  const sku = params.get('sku');
  if (!model) return;
  modelInput.value = sku ? `${model} · SKU ${sku}` : model;
});
